import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";

/**
 * AdminNpsPage.jsx — Página admin dedicada para ver respuestas NPS.
 * 
 * Tabla con filtros (nivel, score_min, score_max, since), columnas:
 * - Fecha
 * - case_id (corto)
 * - user_email
 * - nivel
 * - score (badge con color)
 * - comentario (expandible)
 */
export default function AdminNpsPage() {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [nivel, setNivel] = useState("all");
  const [scoreMin, setScoreMin] = useState("");
  const [scoreMax, setScoreMax] = useState("");
  const [expandedComments, setExpandedComments] = useState(new Set());

  const limit = 20;

  const loadResponses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (nivel !== "all") params.append("nivel", nivel);
      if (scoreMin) params.append("score_min", scoreMin);
      if (scoreMax) params.append("score_max", scoreMax);

      const { data } = await api.get(`/admin/nps/responses?${params.toString()}`);
      setResponses(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudieron cargar las respuestas NPS");
    } finally {
      setLoading(false);
    }
  }, [page, nivel, scoreMin, scoreMax, limit]);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  const toggleCommentExpanded = (id) => {
    setExpandedComments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getScoreBadge = (score) => {
    if (score >= 9) {
      return (
        <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300">
          {score} — Promoter
        </Badge>
      );
    }
    if (score >= 7) {
      return (
        <Badge className="bg-amber-100 text-amber-900 border-amber-300">
          {score} — Passive
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-900 border-red-300">
        {score} — Detractor
      </Badge>
    );
  };

  const pages = Math.ceil(total / limit);

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="admin-nps-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-gray-900">Respuestas NPS</h1>
        <p className="text-sm text-gray-600">{total} respuestas totales</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nivel</label>
              <Select value={nivel} onValueChange={setNivel} data-testid="nps-filter-nivel">
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="nivel1">Diagnóstico (N1)</SelectItem>
                  <SelectItem value="nivel2">Kit Documental (N2)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Score mín</label>
              <input
                type="number"
                min="0"
                max="10"
                value={scoreMin}
                onChange={(e) => setScoreMin(e.target.value)}
                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Score máx</label>
              <input
                type="number"
                min="0"
                max="10"
                value={scoreMax}
                onChange={(e) => setScoreMax(e.target.value)}
                className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm"
                placeholder="10"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setNivel("all");
                  setScoreMin("");
                  setScoreMax("");
                  setPage(1);
                }}
                className="w-full"
              >
                Limpiar filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando respuestas...</div>
      ) : responses.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No hay respuestas NPS con los filtros aplicados.
        </div>
      ) : (
        <div className="space-y-3">
          {responses.map((r) => {
            const isExpanded = expandedComments.has(r.id);
            const hasComment = r.comment && r.comment.trim().length > 0;

            return (
              <Card key={r.id} data-testid={`nps-row-${r.id}`}>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Fecha</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(r.created_at).toLocaleDateString("es-CO", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Caso</p>
                      <p className="text-sm font-mono text-gray-900">{r.case_id.slice(0, 8)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Usuario</p>
                      <p className="text-sm text-gray-900">{r.user_email || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Nivel</p>
                      <Badge variant="outline">{r.nivel === "nivel1" ? "Diagnóstico" : "Kit Documental"}</Badge>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Score</p>
                      {getScoreBadge(r.score)}
                    </div>
                  </div>

                  {hasComment && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <p className="text-xs text-gray-500 mb-1">Comentario</p>
                      <p className={`text-sm text-gray-800 ${isExpanded ? "" : "line-clamp-2"}`}>
                        {r.comment}
                      </p>
                      {r.comment.length > 120 && (
                        <button
                          onClick={() => toggleCommentExpanded(r.id)}
                          className="text-xs text-primary underline mt-1 hover:text-primary/80"
                        >
                          {isExpanded ? "Ver menos" : "Ver más"}
                        </button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Anterior
          </Button>
          <span className="text-sm text-gray-600">
            Página {page} de {pages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page === pages || loading}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
}
